/**
 * bomSpreadsheetStorage.ts
 *
 * Store for BOM Spreadsheet data and BOM Configurations per product.
 * Integrates directly with backend Postgres API (product_bom_configs, product_bom_spreadsheet_data, product_bom_revisions).
 */
import type {
  BomConfigRow,
  BomRevision,
  BomRevisionDiffItem,
  BomSpreadsheetWoodRow,
  BomSpreadsheetHardwareRow,
  ProductBomSpreadsheetData,
} from '../types/bomSpreadsheetTypes'
import {
  getProductBomConfigsApi,
  saveProductBomConfigsApi,
  getProductBomDataApi,
  saveProductBomDataApi,
  getProductBomRevisionsApi,
  createProductBomRevisionApi,
} from '@/services/boms/productBomSpreadsheetApiClient'

export {
  getProductBomConfigsApi,
  saveProductBomConfigsApi,
  getProductBomDataApi,
  saveProductBomDataApi,
  getProductBomRevisionsApi,
  createProductBomRevisionApi,
}

// In-memory runtime cache (no localStorage)
const memoryConfigStore: Record<string, BomConfigRow[]> = {}
const memoryDataStore: Record<string, ProductBomSpreadsheetData> = {}
const memoryRevisionStore: Record<string, BomRevision[]> = {}

export function loadProductBomConfigs(productId: string, productCode: string): BomConfigRow[] {
  const cached = memoryConfigStore[productId]
  if (cached && Array.isArray(cached) && cached.length > 0) {
    return cached.map((item) => ({
      ...item,
      code: productCode,
    }))
  }
  return []
}

export function saveProductBomConfigs(productId: string, configs: BomConfigRow[]): void {
  memoryConfigStore[productId] = configs
  void saveProductBomConfigsApi(productId, configs).catch(() => {})
}

export function loadProductBomData(
  productId: string,
  productCode: string,
  productName: string,
  configCode?: string,
): ProductBomSpreadsheetData {
  const cached = memoryDataStore[productId]
  if (cached && Array.isArray(cached.wood) && Array.isArray(cached.hardware)) {
    return {
      productCode,
      productName,
      configCode: configCode || productCode,
      wood: cached.wood,
      hardware: cached.hardware,
    }
  }

  return {
    productCode,
    productName,
    configCode: configCode || productCode,
    wood: [],
    hardware: [],
  }
}

export function saveProductBomData(productId: string, data: ProductBomSpreadsheetData): void {
  memoryDataStore[productId] = data
}

export const WOOD_FIELD_LABELS: Record<string, string> = {
  part_name: 'Tên chi tiết',
  wood_type_raw: 'Loại gỗ',
  dims_finished_t: 'Dày (T)',
  dims_finished_w: 'Rộng (W)',
  dims_finished_l: 'Dài (L)',
  tenon: 'Mộng',
  qty_per: 'Số lượng 1SP',
  qty_total: 'Số lượng Tổng',
  notes: 'Ghi chú kỹ thuật',
  veneer_spec: 'Quy cách Veneer',
}

export const HW_FIELD_LABELS: Record<string, string> = {
  name_raw: 'Tên vật tư',
  spec_raw: 'Quy cách',
  qty_per: 'Số lượng 1SP',
  qty_total: 'Số lượng Tổng',
  uom_hint: 'Đơn vị tính',
  color_hint: 'Màu sắc',
  item_type: 'Phân loại',
  notes: 'Ghi chú',
}

export function compareBomData(
  oldWood: BomSpreadsheetWoodRow[],
  newWood: BomSpreadsheetWoodRow[],
  oldHw: BomSpreadsheetHardwareRow[],
  newHw: BomSpreadsheetHardwareRow[],
): BomRevisionDiffItem[] {
  const diffs: BomRevisionDiffItem[] = []

  // Wood changes
  const maxWood = Math.max(oldWood.length, newWood.length)
  for (let i = 0; i < maxWood; i++) {
    if (i >= oldWood.length) {
      diffs.push({
        type: 'row_added',
        table: 'wood',
        rowIndex: i + 1,
        rowName: newWood[i].part_name || `Chi tiết ${i + 1}`,
      })
    } else if (i >= newWood.length) {
      diffs.push({
        type: 'row_deleted',
        table: 'wood',
        rowIndex: i + 1,
        rowName: oldWood[i].part_name || `Chi tiết ${i + 1}`,
      })
    } else {
      const o = oldWood[i]
      const n = newWood[i]
      Object.keys(WOOD_FIELD_LABELS).forEach((key) => {
        const ov = String((o as any)[key] ?? '').trim()
        const nv = String((n as any)[key] ?? '').trim()
        if (ov !== nv) {
          diffs.push({
            type: 'cell_change',
            table: 'wood',
            rowIndex: i + 1,
            rowName: n.part_name || o.part_name || `Chi tiết ${i + 1}`,
            field: key,
            fieldLabel: WOOD_FIELD_LABELS[key] || key,
            oldValue: (o as any)[key],
            newValue: (n as any)[key],
          })
        }
      })
    }
  }

  // Hardware changes
  const maxHw = Math.max(oldHw.length, newHw.length)
  for (let i = 0; i < maxHw; i++) {
    if (i >= oldHw.length) {
      diffs.push({
        type: 'row_added',
        table: 'hardware',
        rowIndex: i + 1,
        rowName: newHw[i].name_raw || `Vật tư ${i + 1}`,
      })
    } else if (i >= newHw.length) {
      diffs.push({
        type: 'row_deleted',
        table: 'hardware',
        rowIndex: i + 1,
        rowName: oldHw[i].name_raw || `Vật tư ${i + 1}`,
      })
    } else {
      const o = oldHw[i]
      const n = newHw[i]
      Object.keys(HW_FIELD_LABELS).forEach((key) => {
        const ov = String((o as any)[key] ?? '').trim()
        const nv = String((n as any)[key] ?? '').trim()
        if (ov !== nv) {
          diffs.push({
            type: 'cell_change',
            table: 'hardware',
            rowIndex: i + 1,
            rowName: n.name_raw || o.name_raw || `Vật tư ${i + 1}`,
            field: key,
            fieldLabel: HW_FIELD_LABELS[key] || key,
            oldValue: (o as any)[key],
            newValue: (n as any)[key],
          })
        }
      })
    }
  }

  return diffs
}

export function loadProductBomRevisions(productId: string): BomRevision[] {
  return memoryRevisionStore[productId] || []
}

export function saveProductBomRevisions(productId: string, revisions: BomRevision[]): void {
  memoryRevisionStore[productId] = revisions
}
